import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { FeedPerson, FeedRunCard } from "@signalist/shared-types";

import { api } from "@/redux/api";
import { useAppDispatch } from "@/redux/hooks";

import {
	useDeleteTrackerOrganizationsMutation,
	useRemovePersonFromTrackerOrganizationMutation,
} from "@/pages/Private/redux/profileTracker/profileTracker.api";

/** How long the confirmation / error toast stays up. */
const TOAST_MS = 4000;

interface FeedTrackerRemoval {
	/** Untrack a single person of the card's company (removePersonFromTrackerOrganization). */
	removePerson: (card: FeedRunCard, person: FeedPerson) => Promise<void>;
	/** Stop tracking the whole company (deleteTrackerOrganizations) — drops the card. */
	removeCompany: (card: FeedRunCard) => Promise<void>;
	/** Bottom-center toast copy; null when hidden. */
	toast: { message: string; variant: "success" | "error" } | null;
}

/**
 * Profile-tracker removals from the feed run-card overflow menu (both scopes):
 * a single tracked person, or the whole tracked company. Each refetches the Feed
 * + counts (the affected card leaves the view) and drives a short toast. The
 * confirmation dialogs live in the menu; this hook owns the mutations + toast.
 */
export const useFeedTrackerRemoval = (): FeedTrackerRemoval => {
	const { t } = useTranslation();
	const dispatch = useAppDispatch();
	const [removePersonFromTracker] = useRemovePersonFromTrackerOrganizationMutation();
	const [deleteTrackerOrganizations] = useDeleteTrackerOrganizationsMutation();

	const [toast, setToast] = useState<FeedTrackerRemoval["toast"]>(null);
	const toastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	useEffect(() => {
		if (!toast) {
			return;
		}

		toastTimerRef.current = setTimeout(() => setToast(null), TOAST_MS);

		return () => {
			if (toastTimerRef.current) {
				clearTimeout(toastTimerRef.current);
			}
		};
	}, [toast]);

	const refetchFeed = () => dispatch(api.util.invalidateTags(["Feed", "FeedCounts"]));

	const removePerson = async (card: FeedRunCard, person: FeedPerson) => {
		// Only a tracked lead can be untracked; the menu hides this for non-tracked leads.
		if (person.trackerPersonId == null) {
			return;
		}

		try {
			await removePersonFromTracker({
				id: card.company.trackerOrganizationId,
				personId: person.trackerPersonId,
			}).unwrap();

			refetchFeed();
			setToast({
				message: t("feed.runCard.menu.removePersonToast", { name: person.name ?? "" }),
				variant: "success",
			});
		} catch {
			setToast({ message: t("feed.runCard.menu.removeError"), variant: "error" });
		}
	};

	const removeCompany = async (card: FeedRunCard) => {
		try {
			await deleteTrackerOrganizations({ ids: [card.company.trackerOrganizationId] }).unwrap();

			refetchFeed();
			setToast({
				message: t("feed.runCard.menu.removeCompanyToast", { company: card.company.name ?? "" }),
				variant: "success",
			});
		} catch {
			setToast({ message: t("feed.runCard.menu.removeError"), variant: "error" });
		}
	};

	return { removePerson, removeCompany, toast };
};
